import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Alert from '../../ui/Alert';
import ProgressBar from '../../ui/ProgressBar';
import FeatureScatterPlot from '../FeatureScatterPlot';

export const FeatureDistribution = ({ featureScoreOptions, color, title }) => {
  const [featureScoreData, setFeatureScoreData] = useState([]);
  const {
    features,
    featurePredictions,
    selectedFeatureScoreType,
  } = useSelector(state => state.deepsea);
  const { selectedVariant } = useSelector(state => state.genome);

  // Reset scores before new feature predictions arrive
  useEffect(() => {
    setFeatureScoreData([]);
  }, [selectedFeatureScoreType]);

  useEffect(() => {
    let distributionData = [];
    const featurePrediction = featurePredictions.find(
      variant =>
        selectedVariant &&
        variant.chromosome === selectedVariant.chromosome &&
        variant.ref_allele === selectedVariant.ref_allele &&
        variant.alt_allele === selectedVariant.alt_allele &&
        variant.position === selectedVariant.position,
    );
    if (featurePrediction) {
      distributionData = featurePrediction.feature_predictions
        .map((predictionValue, idx) => ({
          idx,
          y: predictionValue,
        }))
        .sort((a, b) => a.y - b.y)
        .map((prediction, sortedIdx) => ({
          x: sortedIdx,
          y: prediction.y,
          feature: features[prediction.idx],
        }));

      if (selectedFeatureScoreType.non_sig_range) {
        distributionData = distributionData.filter(
          point =>
            point.y < selectedFeatureScoreType.non_sig_range[0] ||
            point.y > selectedFeatureScoreType.non_sig_range[1],
        );
      }
    }
    setFeatureScoreData(distributionData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    featurePredictions,
    selectedVariant.chromosome,
    selectedVariant.position,
    selectedVariant.alt_allele,
    selectedVariant.ref_allele,
  ]);

  const progressBar = <ProgressBar label="Loading features..." />;

  return (
    <div>
      {featureScoreData.length > 0 ? (
        <div>
          {selectedFeatureScoreType.non_sig_range && (
            <Alert bsStyle="warning" style={{ marginTop: '10px' }}>
              Scores between {selectedFeatureScoreType.non_sig_range[0]} and{' '}
              {selectedFeatureScoreType.non_sig_range[1]} have been filtered out
            </Alert>
          )}
          <FeatureScatterPlot
            featureScores={featureScoreData}
            options={featureScoreOptions}
            color={color}
            title={title}
          />
        </div>
      ) : (
        progressBar
      )}
    </div>
  );
};

FeatureDistribution.propTypes = {
  featureScoreOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  color: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

export default FeatureDistribution;
